import { CanonicalLoad, LoadStop } from '@numeo/types-core'
import { useEffect, useRef, useState } from 'react'
import { getItfContactInfo } from '../api/loadboards.api'
import {
    getArriveLoadDetail,
    getBeonLoadDetail,
    getDatLoadDetail,
    getEmergeLoadDetail,
    getNavisphereLoadDetail,
    getSpotLoadDetail,
    getSylectusLoadDetail,
    getTruckStopAccountLoadDetail,
} from '../api/loads.api'
import { fetchUberFreightOfferDetail } from '../api/uber-freight.api'
import { createLoadHubAnalyticsId } from '../utils/analyticsContext'

export interface SylectusDetailData {
    broker: string | null
    email: string | null
    posted: string | null
    expires: string | null
    dockLevel: string | null
    hazmat: string | null
    csaFastLoad: string | null
    postedAmount: string | null
    loadType: string | null
    vehicleRequired: string | null
    pieces: string | null
    weight: string | null
    dimensions: string | null
    stackable: string | null
    notes: string | null
    mc: string | null
    dot: string | null
    pickupLocation: string | null
    pickupDate: string | null
    deliveryLocation: string | null
    deliveryDate: string | null
    stopsInfo: string | null
}

/** Ask the extension background to fetch DAT load detail via the captured session token. */
function fetchDatExtensionLoadDetail(resultId: string): Promise<{
    pickupHours: string | null
    dropOffHours: string | null
    commodity: string | null
    peerRatingSummary: { numOfRatings: number; averageRating: number } | null
    postersReferenceId: string | null
} | null> {
    return new Promise((resolve) => {
        const requestId = `dat-detail-${Date.now()}-${Math.random().toString(36).slice(2)}`
        const timeout = setTimeout(() => {
            window.removeEventListener('message', handler)
            resolve(null)
        }, 15_000)

        const handler = (event: MessageEvent) => {
            if (!event.data || event.data.source !== 'numeo_extension' || event.data.type !== 'DAT_EXTENSION_LOAD_DETAIL_RESULT') return
            if (event.data.payload?.requestId !== requestId) return
            clearTimeout(timeout)
            window.removeEventListener('message', handler)
            resolve(event.data.payload.error ? null : event.data.payload.detail)
        }

        window.addEventListener('message', handler)
        window.postMessage({ source: 'numeo_admin_panel', type: 'DAT_EXTENSION_LOAD_DETAIL', payload: { resultId, requestId } }, '*')
    })
}

export const useLoadDetailFetch = (
    load: CanonicalLoad,
    onLoadUpdate?: (updatedLoad: CanonicalLoad) => void,
    onDetailResolved?: (result: {
        detailRequestId: string
        outcome: 'success' | 'failed' | 'cancelled' | 'timeout'
        latencyMs: number
        errorCode?: string
        resolvedLoad?: CanonicalLoad
    }) => void
) => {
    const contactFetchedRef = useRef(false)
    const detailFetchedRef = useRef(false)
    const mountedRef = useRef(true)
    const [loadDetailLoading, setLoadDetailLoading] = useState(false)
    const [detailStops, setDetailStops] = useState<LoadStop[] | null>(null)
    const [sylectusDetail, setSylectusDetail] = useState<SylectusDetailData | null>(null)

    const hasDetailView =
        load.sourceType === 'BEON' ||
        load.sourceType === 'ARRIVE' ||
        load.sourceType === 'NAVISPHERE' ||
        load.sourceType === 'EMERGE' ||
        load.sourceType === 'SPOT' ||
        load.sourceType === 'SYLECTUS' ||
        load.sourceType === 'DAT' ||
        load.sourceType === 'DAT_EXTENSION' ||
        load.sourceType === 'UBER_FREIGHT' ||
        load.sourceType === 'TRUCKSTOP_ACCOUNT'

    // Track mount state
    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    // Fetch ITF contact info
    useEffect(() => {
        const userKey = load.details?.itfBookedForUserKey
        if (load.sourceType !== 'ITF' || !userKey || contactFetchedRef.current || load.brokerContactName) return
        if (!onLoadUpdate) return

        contactFetchedRef.current = true
        getItfContactInfo(userKey)
            .then((info) => {
                if (!mountedRef.current) return
                const fullName = [info.firstName, info.lastName].filter(Boolean).join(' ') || null
                const phone = info.officePhone ? (info.officePhoneExtension ? `${info.officePhone} x${info.officePhoneExtension}` : info.officePhone) : null
                onLoadUpdate({ ...load, brokerContactName: fullName, brokerContactEmail: info.emailAddress || null, brokerContactPhone: phone })
            })
            .catch(() => {})
    }, [load, onLoadUpdate])

    // Fetch BEON/Arrive/Navisphere load detail
    useEffect(() => {
        if (!hasDetailView || detailFetchedRef.current || !onLoadUpdate) return
        detailFetchedRef.current = true
        setLoadDetailLoading(true)
        const detailRequestId = createLoadHubAnalyticsId('detail_req')
        const detailStartedAt = Date.now()
        let settled = false
        const resolveDetail = (result: { outcome: 'success' | 'failed' | 'cancelled' | 'timeout'; errorCode?: string; resolvedLoad?: CanonicalLoad }): void => {
            if (settled) return
            settled = true
            onDetailResolved?.({ detailRequestId, latencyMs: Date.now() - detailStartedAt, ...result })
        }

        const fetchDetail = async () => {
            try {
                let detail: Partial<CanonicalLoad> | null = null

                if (load.sourceType === 'BEON' && load.externalId) {
                    const loadIdGuid = load.externalId.split('|')[1]
                    if (loadIdGuid) detail = await getBeonLoadDetail(loadIdGuid)
                } else if (load.sourceType === 'ARRIVE' && load.externalId) {
                    const loadNumber = parseInt(load.externalId.split('|')[1], 10)
                    if (!isNaN(loadNumber)) detail = await getArriveLoadDetail(loadNumber)
                } else if (load.sourceType === 'NAVISPHERE' && load.externalId) {
                    const shipmentNumber = parseInt(load.externalId.split('|')[1], 10)
                    if (!isNaN(shipmentNumber)) detail = await getNavisphereLoadDetail(shipmentNumber)
                } else if (load.sourceType === 'EMERGE' && load.externalId) {
                    const loadId = parseInt(load.externalId.split('|')[1], 10)
                    if (!isNaN(loadId)) detail = await getEmergeLoadDetail(loadId)
                } else if (load.sourceType === 'SPOT' && load.externalId) {
                    const shipmentId = load.externalId.split('|')[1]
                    if (shipmentId) detail = await getSpotLoadDetail(shipmentId)
                } else if (load.sourceType === 'TRUCKSTOP_ACCOUNT' && load.externalId) {
                    const loadId = load.externalId.split('|')[1]
                    if (loadId) detail = await getTruckStopAccountLoadDetail(loadId)
                } else if (load.sourceType === 'DAT') {
                    const resultId = (load.details as { result_id?: string } | undefined)?.result_id
                    if (resultId) {
                        const datDetail = await getDatLoadDetail(resultId)
                        if (datDetail && mountedRef.current) {
                            detail = {
                                commodity: datDetail.commodity ?? undefined,
                                details: {
                                    ...load.details,
                                    pickupHours: datDetail.pickupHours,
                                    dropOffHours: datDetail.dropOffHours,
                                    peerRatingNumOfRatings: datDetail.peerRatingSummary?.numOfRatings ?? null,
                                    peerRatingAverage: datDetail.peerRatingSummary?.averageRating ?? null,
                                },
                            } as Partial<CanonicalLoad>
                        }
                    }
                } else if (load.sourceType === 'DAT_EXTENSION') {
                    const resultId = (load.details as { result_id?: string } | undefined)?.result_id
                    if (resultId) {
                        const datDetail = await fetchDatExtensionLoadDetail(resultId)
                        if (datDetail && mountedRef.current) {
                            detail = {
                                commodity: datDetail.commodity ?? undefined,
                                postersReferenceId: datDetail.postersReferenceId ?? load.postersReferenceId ?? null,
                                details: {
                                    ...load.details,
                                    pickupHours: datDetail.pickupHours,
                                    dropOffHours: datDetail.dropOffHours,
                                    peerRatingNumOfRatings: datDetail.peerRatingSummary?.numOfRatings ?? null,
                                    peerRatingAverage: datDetail.peerRatingSummary?.averageRating ?? null,
                                },
                            } as Partial<CanonicalLoad>
                        }
                    }
                } else if (load.sourceType === 'UBER_FREIGHT' && load.externalId) {
                    const offerUUID = load.externalId.split('|')[1]
                    if (offerUUID) {
                        const ufDetail = (await fetchUberFreightOfferDetail(offerUUID)) as {
                            jobDetails?: Array<{
                                commodityName?: string
                                waypoints?: Array<{
                                    appointStartTime?: { epoch?: string; timeZone?: string }
                                    appointEndTime?: { epoch?: string; timeZone?: string }
                                    businessFacility?: {
                                        facilityProfile?: {
                                            name?: string
                                            city?: string
                                            state?: string
                                            postalCode?: string
                                            latitude?: number
                                            longitude?: number
                                            formattedAddress?: string
                                        }
                                    }
                                    note?: string
                                    referenceNumber?: string
                                    businessFacilityRating?: string
                                    tasks?: Array<{ type?: string }>
                                }>
                                formattedRequirements?: { trailerRequirements?: Array<{ name?: string }> }
                                loadNotes?: Array<{ note?: string }>
                            }>
                        } | null
                        if (ufDetail?.jobDetails?.[0] && mountedRef.current) {
                            const job = ufDetail.jobDetails[0]
                            const wps = job.waypoints ?? []
                            const stops: LoadStop[] = wps.map((wp, i) => {
                                const isPickup = wp.tasks?.some((t) => t.type?.includes('PICKUP')) ?? i === 0
                                const profile = wp.businessFacility?.facilityProfile
                                return {
                                    stopType: isPickup ? 'PICKUP' : 'DELIVERY',
                                    stopSequence: i + 1,
                                    stopCity: profile?.city ?? null,
                                    stopStateProv: profile?.state ?? null,
                                    stopPostalCode: profile?.postalCode ?? null,
                                    stopCountryCode: null,
                                    stopLat: profile?.latitude ?? null,
                                    stopLon: profile?.longitude ?? null,
                                    stopWindowStart: wp.appointStartTime?.epoch ? new Date(wp.appointStartTime.epoch) : null,
                                    stopWindowEnd: wp.appointEndTime?.epoch ? new Date(wp.appointEndTime.epoch) : null,
                                    stopTimezone: wp.appointStartTime?.timeZone ?? null,
                                    stopFacilityName: profile?.name ?? null,
                                    stopFacilityCode: null,
                                    stopAppointment: null,
                                    stopInstructions: wp.note || null,
                                    stopFacilityRating: wp.businessFacilityRating || null,
                                    stopFormattedAddress: profile?.formattedAddress || null,
                                }
                            })
                            const requirements = job.formattedRequirements?.trailerRequirements
                                ?.map((r) => r.name)
                                .filter(Boolean)
                                .join(', ')
                            detail = {
                                commodity: job.commodityName ?? undefined,
                                specialRequirements: requirements || undefined,
                                stops,
                                stopsCount: wps.length,
                            } as Partial<CanonicalLoad>
                        }
                    }
                } else if (load.sourceType === 'SYLECTUS' && load.details?.sylectusBidUrl) {
                    const url = new URL(load.details.sylectusBidUrl)
                    const pronumuk = url.searchParams.get('pronumuk')
                    const pronum = url.searchParams.get('pronum')
                    const loadmabcode = url.searchParams.get('loadmabcode')
                    const loadsf = url.searchParams.get('loadsf')
                    if (pronumuk && pronum && loadmabcode && loadsf) {
                        const sylDetail = await getSylectusLoadDetail(pronumuk, pronum, loadmabcode, loadsf)
                        if (sylDetail && mountedRef.current) {
                            setSylectusDetail(sylDetail as unknown as SylectusDetailData)
                            detail = {
                                brokerCompanyName: sylDetail.broker ?? undefined,
                                brokerContactEmail: sylDetail.email ?? undefined,
                                brokerMc: sylDetail.mc ?? undefined,
                                brokerDot: sylDetail.dot ?? undefined,
                                comment: sylDetail.notes ?? undefined,
                            } as Partial<CanonicalLoad>
                        }
                    }
                }

                if (!mountedRef.current) return

                let resolvedLoad = load
                if (detail) {
                    if (detail.stops) setDetailStops(detail.stops as LoadStop[])
                    resolvedLoad = {
                        ...load,
                        commodity: detail.commodity ?? load.commodity,
                        postersReferenceId: detail.postersReferenceId ?? load.postersReferenceId,
                        brokerCompanyName: detail.brokerCompanyName ?? load.brokerCompanyName,
                        brokerContactName: detail.brokerContactName ?? load.brokerContactName,
                        brokerContactEmail: detail.brokerContactEmail ?? load.brokerContactEmail,
                        brokerContactPhone: detail.brokerContactPhone ?? load.brokerContactPhone,
                        specialRequirements: detail.specialRequirements ?? load.specialRequirements,
                        comment: detail.comment ?? load.comment,
                        rateAmountUsd: detail.rateAmountUsd ?? load.rateAmountUsd,
                        ratePerMileUsd: detail.ratePerMileUsd ?? load.ratePerMileUsd,
                        equipmentType: detail.equipmentType ?? load.equipmentType,
                        equipmentLengthFt: detail.equipmentLengthFt ?? load.equipmentLengthFt,
                        fullPartial: detail.fullPartial ?? load.fullPartial,
                        weightLbs: detail.weightLbs ?? load.weightLbs,
                        distanceMiles: detail.distanceMiles ?? load.distanceMiles,
                        stopsCount: detail.stopsCount ?? load.stopsCount,
                        details: {
                            ...load.details,
                            ...detail.details,
                            driverShouldUnload: detail.details?.driverShouldUnload ?? load.details?.driverShouldUnload ?? null,
                        },
                    }
                    onLoadUpdate(resolvedLoad)
                }
                resolveDetail({
                    outcome: detail ? 'success' : 'failed',
                    ...(detail ? { resolvedLoad } : {}),
                    ...(detail ? {} : { errorCode: 'detail_unavailable' }),
                })
            } catch (error) {
                console.error('Load detail fetch error:', error)
                resolveDetail({
                    outcome: /timeout|timed out/i.test(error instanceof Error ? error.message : '') ? 'timeout' : 'failed',
                    errorCode: /timeout|timed out/i.test(error instanceof Error ? error.message : '') ? 'detail_timeout' : 'detail_request_failed',
                })
            } finally {
                if (mountedRef.current) setLoadDetailLoading(false)
            }
        }
        void fetchDetail()
        return () => resolveDetail({ outcome: 'cancelled', errorCode: 'detail_view_closed' })
    }, [hasDetailView, load, onLoadUpdate, onDetailResolved])

    return { hasDetailView, loadDetailLoading, detailStops, sylectusDetail }
}
